import { ActionReducerMapBuilder, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { DEFAULT_PAGINATION_SETTINGS } from 'app/constants';
import { DEFAULT_PAGE_SIZE } from 'app/constants/api';
import { EntityStoreName } from 'app/enums/store';
import { UsersListType } from '@okletsplay/core';
import { Pagination } from 'app/interfaces';
import { PaginatedResponse } from 'app/interfaces/api';
import { UserEntity } from 'app/interfaces/entity';
import { apiRequestCall } from 'app/services/api';
import { ApiResource } from 'app/services/api/enums';
import { cancelationSubscribe } from 'app/services/api/utils';
import { entityStoreStatus } from 'app/utils/store/entity-store-status';
import { UpdateUserConversationsRequest, UpdateUserRequest } from 'modules/users/interfaces';
import { UsersState } from 'modules/users/store/users/interfaces';
import { UsersStoreStatus } from 'modules/users/store/users/enums';
import { store } from 'redux/store';

import { entityAdapter } from 'modules/users/store/users/adapter';

export const getUsersList = createAsyncThunk(
  `${EntityStoreName.Users}/getUsersList`,
  (
    params: { search: string; type: UsersListType },
    { signal }
  ): Promise<PaginatedResponse<UserEntity[]>> => {
    const cancelToken = cancelationSubscribe(signal);
    const {
      limit = DEFAULT_PAGE_SIZE,
      offset = 0,
      reload = true,
    } = store.getState()[EntityStoreName.Users].pagination[params.type as string] ??
    DEFAULT_PAGINATION_SETTINGS;

    return apiRequestCall<PaginatedResponse<UserEntity[]>>({
      params: {
        method: 'GET',
        resource: ApiResource.Users,
        cancelToken,
        params: {
          limit: reload ? Math.max(offset, limit) : limit,
          offset: reload ? 0 : offset,
          ...params,
        },
      },
    });
  }
);

export const updateUser = createAsyncThunk(
  `${EntityStoreName.Users}/updateUser`,
  ({ identifier, data }: UpdateUserRequest): Promise<UserEntity> => {
    return apiRequestCall<UserEntity>({
      params: {
        method: 'PATCH',
        resource: ApiResource.Users,
        urlPrefix: identifier,
        data,
      },
    });
  }
);

export const updateUserConversation = createAsyncThunk(
  `${EntityStoreName.Users}/updateUserConversation`,
  (data: UpdateUserConversationsRequest): Promise<UserEntity> => {
    return apiRequestCall<UserEntity>({
      params: {
        method: 'POST',
        resource: ApiResource.Users,
        urlPrefix: 'conversations',
        data,
      },
    });
  }
);

export const updatePaginationReducer = (
  state: UsersState,
  action: PayloadAction<{ identifier?: UsersListType; data: Partial<Pagination> }>
): void => {
  if (!action.payload.identifier) {
    return;
  }
  state.pagination = {
    ...state.pagination,
    [action.payload.identifier]: {
      ...DEFAULT_PAGINATION_SETTINGS,
      ...state.pagination[action.payload.identifier],
      ...action.payload.data,
    },
  };
};

export const clearAllReducer = (state: UsersState): void => {
  entityAdapter.removeAll(state);
};

export const registerReducers = (builder: ActionReducerMapBuilder<UsersState>): void => {
  builder
    .addCase(getUsersList.pending, (state, action) => {
      entityStoreStatus.addRequestId(state, UsersStoreStatus.Loading, action.meta.requestId);
    })
    .addCase(getUsersList.fulfilled, (state, action) => {
      const type = action.meta.arg.type as string;
      const reload = state.pagination[type]?.reload ?? true;

      if (reload) {
        entityAdapter.setAll(state, action.payload.data);
      } else {
        entityAdapter.addMany(state, action.payload.data);
      }

      if (state.pagination[type]) {
        state.pagination[type].total = action.payload.total;
        state.pagination[type].offset = reload
          ? action.payload.data.length
          : state.pagination[type].offset + action.payload.data.length;
      }

      entityStoreStatus.removeRequestId(state, UsersStoreStatus.Loading, action.meta.requestId);
    })
    .addCase(getUsersList.rejected, (state, action) => {
      entityStoreStatus.removeRequestId(state, UsersStoreStatus.Loading, action.meta.requestId);
    })
    .addCase(updateUser.pending, (state, action) => {
      entityStoreStatus.addRequestId(state, UsersStoreStatus.Updating, action.meta.requestId);
    })
    .addCase(updateUser.fulfilled, (state, action) => {
      entityStoreStatus.removeRequestId(state, UsersStoreStatus.Updating, action.meta.requestId);
    })
    .addCase(updateUser.rejected, (state, action) => {
      entityStoreStatus.removeRequestId(state, UsersStoreStatus.Updating, action.meta.requestId);
    })
    .addCase(updateUserConversation.pending, (state, action) => {
      entityStoreStatus.addRequestId(state, UsersStoreStatus.Updating, action.meta.requestId);
    })
    .addCase(updateUserConversation.fulfilled, (state, action) => {
      entityStoreStatus.removeRequestId(state, UsersStoreStatus.Updating, action.meta.requestId);
    })
    .addCase(updateUserConversation.rejected, (state, action) => {
      entityStoreStatus.removeRequestId(state, UsersStoreStatus.Updating, action.meta.requestId);
    });
};
